import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "../../../services/api";
import useAuthenticate from "../../../hooks/useAuthenticate";
import Navbar from "src/components/organisms/navbar";
import Footer from "src/components/organisms/footer";
import {
  PageLocationTitle,
  SummaryCard,
  SummaryValue,
  SummaryLabel,
  ObsText,
} from "./styles";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

interface Tournament {
  id: number;
  name: string;
  slug: string;
  authorId?: number;
}

interface PaymentEntry {
  id: number;
  name: string;
  gym: string;
  value: string;
  observation: string;
  category: string;
  belt: string;
  weight: string;
  tournamentId: number;
}

interface ReportData {
  payments: PaymentEntry[];
  summary: {
    totalPayments: number;
    totalValue: number;
  };
}

const ManualPaymentsReport = () => {
  const { account, getToken }: any = useAuthenticate();
  const location = useLocation();

  const isAdminRoute = location.pathname.startsWith("/admin/");
  const preSelectedTournaments = (location.state as any)?.selectedTournaments;
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [selectedTournaments, setSelectedTournaments] = useState<Tournament[]>(
    preSelectedTournaments || []
  );
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingTournaments, setLoadingTournaments] = useState(true);
  const [searchTable, setSearchTable] = useState("");
  const [autoFetched, setAutoFetched] = useState(false);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  useEffect(() => {
    if (!account) return;
    fetchTournaments();
  }, [account]);

  // Auto-buscar se veio com torneios pré-selecionados
  useEffect(() => {
    if (preSelectedTournaments && preSelectedTournaments.length > 0 && !autoFetched && !loadingTournaments) {
      setAutoFetched(true);
      fetchReport();
    }
  }, [loadingTournaments]);

  const fetchTournaments = async () => {
    try {
      const response = await api.get("/managers/tournaments/info", {
        headers: { Authorization: `Bearer ${getToken() || ""}` },
      });

      let data = response.data;

      if (!isAdminRoute) {
        data = data.filter((t: any) => t.authorId === account?.id);
      }

      const sorted = [...data].sort((a: any, b: any) =>
        a.name.localeCompare(b.name, "pt-BR")
      );
      setTournaments(sorted);
    } catch (err) {
      console.error(err);
      toast.error("Erro ao carregar torneios");
    } finally {
      setLoadingTournaments(false);
    }
  };

  const fetchReport = async () => {
    if (selectedTournaments.length === 0) {
      toast.warn("Selecione ao menos um torneio");
      return;
    }

    setLoading(true);
    setSearchTable("");
    setSortField(null);
    setSortDirection("asc");
    try {
      const ids = selectedTournaments.map((t) => t.id).join(",");
      const basePath = isAdminRoute
        ? "/administrators/tournaments/report/manual-payments"
        : "/managers/tournaments/report/manual-payments";

      const response = await api.get(`${basePath}?tournaments=${ids}`, {
        headers: { Authorization: `Bearer ${getToken() || ""}` },
      });

      setReportData(response.data);
    } catch (err) {
      console.error(err);
      toast.error("Erro ao buscar dados do relatório");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };

  const exportToXLSX = () => {
    if (!reportData) return;

    const tournamentMap = new Map(selectedTournaments.map((t) => [t.id, t.name]));

    const wsData = reportData.payments.map((p, i) => ({
      "#": i + 1,
      "N. Insc.": p.id,
      Nome: p.name,
      Academia: p.gym,
      Valor: p.value,
      Categoria: p.category,
      Faixa: p.belt,
      Peso: p.weight,
      "Observação": p.observation,
      Torneio: tournamentMap.get(p.tournamentId) || p.tournamentId,
    }));

    const ws = XLSX.utils.json_to_sheet(wsData);
    ws["!cols"] = [
      { wch: 5 }, { wch: 10 }, { wch: 30 }, { wch: 25 }, { wch: 12 },
      { wch: 20 }, { wch: 12 }, { wch: 12 }, { wch: 40 }, { wch: 30 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Pagamentos Manuais");
    XLSX.writeFile(wb, "pagamentos-manuais.xlsx");
  };

  const highlightText = (text: string, term: string) => {
    if (!term) return text;
    const idx = text.toUpperCase().indexOf(term.toUpperCase());
    if (idx === -1) return text;
    const before = text.slice(0, idx);
    const match = text.slice(idx, idx + term.length);
    const after = text.slice(idx + term.length);
    return (
      <>
        {before}
        <span style={{ backgroundColor: "#fef08a", borderRadius: 2, padding: "0 1px" }}>
          {match}
        </span>
        {after}
      </>
    );
  };

  const tournamentMap = new Map(selectedTournaments.map((t) => [t.id, t.name]));

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const filteredPayments = (() => {
    let result = reportData?.payments.filter((p) => {
      if (!searchTable) return true;
      const term = searchTable.toUpperCase();
      return (
        p.name.toUpperCase().includes(term) ||
        p.gym.toUpperCase().includes(term) ||
        (p.observation && p.observation.toUpperCase().includes(term)) ||
        p.category.toUpperCase().includes(term)
      );
    });

    if (result && sortField) {
      result = [...result].sort((a, b) => {
        const dir = sortDirection === "asc" ? 1 : -1;
        switch (sortField) {
          case "id": return (a.id - b.id) * dir;
          case "name": return a.name.localeCompare(b.name, "pt-BR") * dir;
          case "gym": return a.gym.localeCompare(b.gym, "pt-BR") * dir;
          case "value": {
            const va = parseFloat(a.value) || 0;
            const vb = parseFloat(b.value) || 0;
            return (va - vb) * dir;
          }
          case "category": return a.category.localeCompare(b.category, "pt-BR") * dir;
          case "observation": return (a.observation || "").localeCompare(b.observation || "", "pt-BR") * dir;
          case "tournament": {
            const ta = tournamentMap.get(a.tournamentId) || "";
            const tb = tournamentMap.get(b.tournamentId) || "";
            return ta.localeCompare(tb, "pt-BR") * dir;
          }
          default: return 0;
        }
      });
    }

    return result;
  })();

  return (
    <>
      <Navbar />
      <Box sx={{ backgroundColor: "#f3f4f8", minHeight: "100vh", pb: 6 }}>
        <Container maxWidth="lg" sx={{ pt: 4 }}>
          <PageLocationTitle>Pagamentos Manuais</PageLocationTitle>

          {/* Filtros */}
          <Paper
            elevation={0}
            sx={{
              mt: 3,
              p: 3,
              borderRadius: "12px",
              border: "1px solid rgba(26,34,82,0.06)",
            }}
          >
            <Autocomplete
              multiple
              options={tournaments}
              getOptionLabel={(option) => option.name}
              value={selectedTournaments}
              onChange={(_, newValue) => setSelectedTournaments(newValue)}
              loading={loadingTournaments}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Selecione os torneios"
                  placeholder="Buscar torneio..."
                  size="small"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={option.name}
                    size="small"
                    sx={{
                      backgroundColor: "#1a2252",
                      color: "#fff",
                      fontWeight: 500,
                      "& .MuiChip-deleteIcon": { color: "rgba(255,255,255,0.7)" },
                    }}
                  />
                ))
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Button
                variant="contained"
                onClick={fetchReport}
                disabled={loading || selectedTournaments.length === 0}
                sx={{
                  backgroundColor: "#1a2252",
                  textTransform: "none",
                  fontWeight: 600,
                  px: 4,
                  borderRadius: "8px",
                  "&:hover": { backgroundColor: "#243070" },
                }}
              >
                {loading ? (
                  <CircularProgress size={20} sx={{ color: "#fff" }} />
                ) : (
                  "Buscar"
                )}
              </Button>
            </Box>
          </Paper>

          {/* Summary Cards */}
          {reportData && (
            <>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                  mt: 3,
                }}
              >
                <SummaryCard>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                    <SummaryLabel>Total de Pagamentos Manuais</SummaryLabel>
                    <SummaryValue>
                      <span translate="no">{reportData.summary.totalPayments}</span>
                    </SummaryValue>
                  </Box>
                </SummaryCard>

                <SummaryCard>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                    <SummaryLabel>Valor Total</SummaryLabel>
                    <SummaryValue>
                      <span translate="no">
                        {formatCurrency(reportData.summary.totalValue)}
                      </span>
                    </SummaryValue>
                  </Box>
                </SummaryCard>
              </Box>

              {/* Busca + exportar */}
              <Box
                sx={{
                  mt: 3,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <TextField
                  size="small"
                  placeholder="Buscar por nome, academia ou observação..."
                  value={searchTable}
                  onChange={(e) => setSearchTable(e.target.value)}
                  sx={{
                    flex: 1,
                    minWidth: 200,
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                    "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                  }}
                />
                <Tooltip title="Exportar para Excel">
                  <Button
                    variant="outlined"
                    onClick={exportToXLSX}
                    sx={{
                      borderColor: "#1a2252",
                      color: "#1a2252",
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: "8px",
                      "&:hover": {
                        backgroundColor: "#1a2252",
                        color: "#fff",
                      },
                    }}
                  >
                    Exportar XLSX
                  </Button>
                </Tooltip>
              </Box>

              {/* Tabela */}
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  mt: 2,
                  mb: 4,
                  borderRadius: "12px",
                  border: "1px solid rgba(26,34,82,0.06)",
                  overflow: "hidden",
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#1a2252" }}>
                      {([
                        { label: "#", field: null, align: "center" as const },
                        { label: "N. Insc.", field: "id", align: "center" as const },
                        { label: "Nome", field: "name", align: "left" as const },
                        { label: "Academia", field: "gym", align: "left" as const },
                        { label: "Valor", field: "value", align: "center" as const },
                        { label: "Categoria", field: "category", align: "left" as const },
                        { label: "Observação", field: "observation", align: "left" as const },
                        { label: "Torneio", field: "tournament", align: "left" as const },
                      ]).map((col) => (
                          <TableCell
                            key={col.label}
                            sx={{
                              color: "#fff",
                              fontWeight: 700,
                              fontSize: "12px",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              py: 1.5,
                              whiteSpace: "nowrap",
                              "& .MuiTableSortLabel-root": { color: "#fff !important" },
                              "& .MuiTableSortLabel-root.Mui-active": { color: "#fff !important" },
                              "& .MuiTableSortLabel-icon": { color: "#fff !important", opacity: 0.7 },
                            }}
                            align={col.align}
                          >
                            {col.field ? (
                              <TableSortLabel
                                active={sortField === col.field}
                                direction={sortField === col.field ? sortDirection : "asc"}
                                onClick={() => handleSort(col.field!)}
                              >
                                {col.label}
                              </TableSortLabel>
                            ) : (
                              col.label
                            )}
                          </TableCell>
                        )
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredPayments && filteredPayments.length > 0 ? (
                      filteredPayments.map((payment, index) => (
                        <TableRow
                          key={payment.id}
                          sx={{
                            backgroundColor: index % 2 === 0 ? "#fff" : "#f7f8fb",
                            "&:hover": { backgroundColor: "#f0f2f8" },
                          }}
                        >
                          <TableCell
                            align="center"
                            sx={{ color: "#6b7490", fontSize: "13px", width: 40 }}
                          >
                            {index + 1}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ fontWeight: 700, fontSize: "13px", color: "#1a2252", width: 70 }}
                          >
                            {payment.id}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              fontSize: "13px",
                              color: "#1a2252",
                              maxWidth: 200,
                            }}
                          >
                            {highlightText(payment.name, searchTable)}
                          </TableCell>
                          <TableCell sx={{ fontSize: "13px", color: "#353d58" }}>
                            {highlightText(payment.gym, searchTable)}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ whiteSpace: "nowrap" }}
                          >
                            {payment.value ? (
                              <Chip
                                label={`R$ ${payment.value}`}
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "12px",
                                  backgroundColor: "#dcfce7",
                                  color: "#166534",
                                }}
                              />
                            ) : (
                              <Typography
                                sx={{ fontSize: "12px", color: "#9098b0" }}
                              >
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: "12px", color: "#4e5672" }}>
                            <Box>
                              {highlightText(payment.category, searchTable)}
                              {payment.belt && (
                                <Typography
                                  sx={{
                                    fontSize: "11px",
                                    color: "#9098b0",
                                  }}
                                >
                                  {payment.belt}
                                  {payment.weight ? ` / ${payment.weight}` : ""}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 300 }}>
                            {payment.observation ? (
                              <ObsText>
                                {highlightText(payment.observation, searchTable)}
                              </ObsText>
                            ) : (
                              <Typography
                                sx={{ fontSize: "12px", color: "#c4c9d8" }}
                              >
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: "12px", color: "#6b7490", maxWidth: 180 }}>
                            {tournamentMap.get(payment.tournamentId) || payment.tournamentId}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={8}
                          align="center"
                          sx={{ py: 4, color: "#9098b0" }}
                        >
                          {searchTable
                            ? "Nenhum resultado para a busca"
                            : "Nenhum dado encontrado"}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {/* Estado vazio */}
          {!reportData && !loading && (
            <Box
              sx={{
                mt: 6,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                color: "#9098b0",
              }}
            >
              <Typography sx={{ fontSize: "16px", fontWeight: 500 }}>
                Selecione os torneios e clique em Buscar para gerar o relatório
              </Typography>
            </Box>
          )}
        </Container>
      </Box>
      <Footer />
    </>
  );
};

export default ManualPaymentsReport;
